import { GlIcon, GlLink, GlButton } from '@gitlab/ui';
import { translate } from '../../../../../utils/i18n';
import MessageToolApproval from '../../duo_chat_message_tool_approval/message_tool_approval';
import { APPROVAL_TOOL_NAMES } from '../../../constants';
import MessageToolDetails from './message_tool_details';
import BaseMessage from './message_base';
import __vue_normalize__ from 'vue-runtime-helpers/dist/normalize-component.js';

const i18n = {
  PROJECT_LABEL: translate('DuoChatMessage.projectLabel', 'Project'),
  BRANCH_LABEL: translate('DuoChatMessage.branchLabel', 'Branch'),
  START_BRANCH_LABEL: translate('DuoChatMessage.startBranchLabel', 'Start branch'),
  COMMIT_MESSAGE_LABEL: translate('DuoChatMessage.commitMessageLabel', 'Commit message'),
  ACTION_LABEL: translate('DuoChatMessage.actionLabel', 'Action'),
  CONTENT_LABEL: translate('DuoChatMessage.contentLabel', 'Content'),
  FILE_PATH_LABEL: translate('DuoChatMessage.filePathLabel', 'File path'),
  RAN_TERMINAL_COMMAND: translate('DuoChatMessage.ranTerminalCommand', 'Ran terminal command')
};

// Hide details section for specified subtypes
const hiddenSubTypes = ['command_output'];
var script = {
  name: 'DuoToolMessage',
  components: {
    BaseMessage,
    MessageToolApproval,
    MessageToolDetails,
    GlIcon,
    GlLink,
    GlButton
  },
  i18n,
  provide: {
    renderGFM: {
      from: 'renderGFM',
      default: () => element => {
        element.classList.add('gl-markdown', 'gl-compact-markdown');
        element.classList.add('duo-system-message', 'gl-text-sm');
      }
    }
  },
  inject: {
    detailsExpanded: {
      default: false
    }
  },
  props: {
    message: {
      required: true,
      type: Object,
      validate: value => {
        return value && value.tool_info;
      }
    },
    workingDirectory: {
      required: false,
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isDetailsOpen: this.detailsExpanded
    };
  },
  computed: {
    iconName() {
      switch (this.toolName) {
        case 'read_file':
          return 'eye';
        case 'write_file':
          return 'pencil';
        case 'grep_files':
        case 'list_files':
          return 'search';
        case 'get_issue':
        case 'list_issue_notes':
          return 'issues';
        case 'create_commit':
          return 'commit';
        default:
          return 'issue-type-maintenance';
      }
    },
    messageFilePath() {
      var _this$message$tool_in, _this$message$tool_in2;
      return (_this$message$tool_in = this.message.tool_info) === null || _this$message$tool_in === void 0 ? void 0 : (_this$message$tool_in2 = _this$message$tool_in.args) === null || _this$message$tool_in2 === void 0 ? void 0 : _this$message$tool_in2.file_path;
    },
    toolArgs() {
      var _this$message$tool_in3;
      return ((_this$message$tool_in3 = this.message.tool_info) === null || _this$message$tool_in3 === void 0 ? void 0 : _this$message$tool_in3.args) || {};
    },
    projectId() {
      return this.toolArgs.project_id;
    },
    branchName() {
      return this.toolArgs.branch;
    },
    startBranch() {
      return this.toolArgs.start_branch;
    },
    commitMessage() {
      return this.toolArgs.commit_message;
    },
    hasCommandOutput() {
      return this.message.message_sub_type === 'command_output';
    },
    hasToolInfoArgs() {
      return Object.keys(this.toolArgs).length > 0;
    },
    hasToolInfoResponse() {
      return Object.prototype.hasOwnProperty.call(this.message.tool_info || {}, 'tool_response');
    },
    toolName() {
      var _this$message$tool_in4;
      return (_this$message$tool_in4 = this.message.tool_info) === null || _this$message$tool_in4 === void 0 ? void 0 : _this$message$tool_in4.name;
    },
    toolResponseContent() {
      var _this$message$tool_in5, _this$message$tool_in6;
      return (_this$message$tool_in5 = this.message.tool_info) === null || _this$message$tool_in5 === void 0 ? void 0 : (_this$message$tool_in6 = _this$message$tool_in5.tool_response) === null || _this$message$tool_in6 === void 0 ? void 0 : _this$message$tool_in6.content;
    },
    toolWorkingDirectory() {
      return this.workingDirectory;
    },
    collapseIconName() {
      return this.isDetailsOpen ? 'chevron-down' : 'chevron-right';
    },
    requiresApproval() {
      return Object.values(APPROVAL_TOOL_NAMES).includes(this.toolName);
    },
    shouldShowDetails() {
      var _this$message;
      return !hiddenSubTypes.includes((_this$message = this.message) === null || _this$message === void 0 ? void 0 : _this$message.message_sub_type) && (this.hasToolInfoArgs || this.hasToolInfoResponse);
    }
  },
  methods: {
    onOpenFilePath(filePath) {
      const pathToEmit = filePath || this.messageFilePath;
      if (pathToEmit) {
        this.$emit('open-file-path', pathToEmit);
      }
    },
    toggleClickToolDetails() {
      if (!this.shouldShowDetails) return; // don't act as accordion if details are hidden
      this.isDetailsOpen = !this.isDetailsOpen;
    },
    toggleKeydownToolDetails(e) {
      if (!this.shouldShowDetails) return; // don't act as accordion if details are hidden
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.isDetailsOpen = !this.isDetailsOpen;
      }
    },
    onCopyCodeSnippet(e) {
      /**
       * Emit copy-code-snippet event that clients can use to interact with a suggested code.
       * @param {*} event An event containing code string in the "detail.code" field.
       */
      this.$emit('copy-code-snippet', e);
    }
  }
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.requiresApproval)?_c('message-tool-approval',{attrs:{"message":_vm.message,"working-directory":_vm.workingDirectory,"approval-status":"approved"}}):_c('base-message',{attrs:{"message":_vm.message},scopedSlots:_vm._u([{key:"message",fn:function(ref){
var content = ref.content;
return [_c('div',{staticClass:"gl-flex gl-items-baseline !gl-text-subtle"},[_c('div',{staticClass:"gl-mb-2"},[(_vm.shouldShowDetails)?_c('gl-button',{attrs:{"button-text-classes":"!gl-whitespace-normal gl-text-left","icon":_vm.iconName,"variant":"link"},on:{"click":_vm.toggleClickToolDetails}},[_c('span',{attrs:{"data-testid":"tool-message-content"}},[_vm._v(_vm._s(content))]),_vm._v(" "),(_vm.shouldShowDetails)?_c('gl-icon',{attrs:{"name":_vm.collapseIconName}}):_vm._e()],1):_c('div',{staticClass:"gl-flex gl-items-center"},[_c('span',{staticClass:"gl-mr-3 gl-flex-shrink-0"},[_c('gl-icon',{attrs:{"name":_vm.iconName}})],1),_vm._v(" "),_c('span',{attrs:{"data-testid":"tool-message-content"}},[_vm._v(_vm._s(content))])])],1)]),_vm._v(" "),(_vm.projectId)?_c('div',{staticClass:"gl-mb-2",attrs:{"data-testid":"tool-message-project-info"}},[_c('span',{staticClass:"gl-font-weight-bold"},[_vm._v(_vm._s(_vm.$options.i18n.PROJECT_LABEL)+":")]),_vm._v(" "),_c('span',{staticClass:"gl-ml-2",attrs:{"data-testid":"tool-message-project-id"}},[_vm._v(_vm._s(_vm.projectId))])]):_vm._e(),_vm._v(" "),(_vm.branchName)?_c('div',{staticClass:"gl-mb-2",attrs:{"data-testid":"tool-message-branch-info"}},[_c('span',{staticClass:"gl-font-weight-bold"},[_vm._v(_vm._s(_vm.$options.i18n.BRANCH_LABEL)+":")]),_vm._v(" "),_c('span',{staticClass:"gl-ml-2",attrs:{"data-testid":"tool-message-branch-name"}},[_vm._v(_vm._s(_vm.branchName))])]):_vm._e(),_vm._v(" "),(_vm.startBranch)?_c('div',{staticClass:"gl-mb-2",attrs:{"data-testid":"tool-message-start-branch-info"}},[_c('span',{staticClass:"gl-font-weight-bold"},[_vm._v(_vm._s(_vm.$options.i18n.START_BRANCH_LABEL)+":")]),_vm._v(" "),_c('span',{staticClass:"gl-ml-2",attrs:{"data-testid":"tool-message-start-branch-name"}},[_vm._v(_vm._s(_vm.startBranch))])]):(_vm.messageFilePath)?_c('div',{staticClass:"gl-mb-2"},[_c('span',{staticClass:"gl-font-weight-bold"},[_vm._v(_vm._s(_vm.$options.i18n.FILE_PATH_LABEL)+":")]),_vm._v(" "),_c('gl-link',{staticClass:"gl-markdown file-path-link gl-ml-2 gl-break-all",attrs:{"data-testid":"tool-message-file-path-link"},on:{"click":function($event){$event.preventDefault();return _vm.onOpenFilePath(_vm.messageFilePath)}}},[_c('code',[_vm._v(_vm._s(_vm.messageFilePath))])])],1):_vm._e(),_vm._v(" "),(_vm.shouldShowDetails)?_c('message-tool-details',{attrs:{"message":_vm.message,"is-expanded":_vm.isDetailsOpen},on:{"copy-code-snippet":_vm.onCopyCodeSnippet}}):_vm._e()]}}])})};
var __vue_staticRenderFns__ = [];

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = __vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export default __vue_component__;
export { i18n };
